import DOMPurify from 'dompurify';
import { Marked, type Tokens } from 'marked';

// Links leave the app in a new tab without handing it window.opener.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Raw HTML in the source is shown as text, never rendered.
const marked = new Marked({
  gfm: true,
  breaks: true,
  async: false,
  renderer: { html: ({ text }) => escapeHtml(text) },
});

/** GitHub-flavoured markdown → sanitized HTML. */
export const renderMarkdown = (source: string): string =>
  DOMPurify.sanitize(marked.parse(source) as string, {
    FORBID_TAGS: ['style', 'iframe', 'form', 'input'],
  });

/** Plain-text preview of markdown for one-line summaries (list cards). */
export const stripMarkdown = (source: string): string =>
  source
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#*_>`~|-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** First markdown image becomes the poll cover; `body` is the source without it. */
export const splitCover = (source: string): { cover: string | null; alt: string; body: string } => {
  let image: Tokens.Image | undefined;
  marked.walkTokens(marked.lexer(source), (token) => {
    if (!image && token.type === 'image') image = token as Tokens.Image;
  });
  return image
    ? { cover: image.href, alt: image.text, body: source.replace(image.raw, '').trim() }
    : { cover: null, alt: '', body: source };
};
